import type { Landmark } from '../../../data/types';
import type { Ring, Vec2 } from '../../../geo/types';
import { corners } from './genericBlock';
import { toLayout, type PlanFrame } from './planFrame';
import reading from './lucienerfPlan.json';

export const LUCIENERF = reading;

function partRect(part: { readonly length: number; readonly width: number }) {
  return { u0: -part.length / 2, u1: part.length / 2, v0: -part.width / 2, v1: part.width / 2 };
}

export function lucienerfFrame(landmark: Landmark): PlanFrame {
  const x = landmark.params['anchorX'],
    y = landmark.params['anchorY'];
  if (typeof x !== 'number' || typeof y !== 'number')
    throw new Error(`${landmark.id} lacks its carpenter-house anchor`);
  return { origin: [x, y], heading: landmark.heading, ring: [] };
}

export function lucienerfPartFrame(
  plan: PlanFrame,
  part: { readonly u: number; readonly v: number; readonly angleDeg?: number }
): PlanFrame {
  return {
    origin: toLayout(plan, [part.u, part.v]),
    heading: plan.heading + ((part.angleDeg ?? 0) * Math.PI) / 180,
    ring: [],
  };
}

/** Only the house and its attached front section receive masonry foundations. */
export function lucienerfFoundations(landmark: Landmark): Ring[] {
  const plan = lucienerfFrame(landmark);
  return reading.houses.map((p) =>
    corners(partRect(p)).map((q) => toLayout(lucienerfPartFrame(plan, p), q))
  );
}

/** Stored timber blocks walking locally; the working yard remains open. */
export function lucienerfObstacles(landmark: Landmark): Ring[] {
  const plan = lucienerfFrame(landmark);
  return [
    ...lucienerfFoundations(landmark),
    ...reading.timber.map((p) =>
      corners(partRect(p)).map((q) => toLayout(lucienerfPartFrame(plan, p), q))
    ),
  ];
}

export function lucienerfStockCentre(
  landmark: Landmark,
  stock: { readonly u: number; readonly v: number }
): Vec2 {
  return toLayout(lucienerfFrame(landmark), [stock.u, stock.v]);
}
